import React, { useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Isotipo from "@/components/Isotipo";
import IntroSplash from "../IntroSplash";
import MockupGateway from "../MockupGateway";
import VariantSwitcher from "../VariantSwitcher";
import { CONTACT } from "../data";
import { mont, body, COLORS } from "./ui";

const THEME = { bg: COLORS.bg, text: COLORS.ink, accent: COLORS.gold, sub: COLORS.green };

const NAV = [
  ["Inicio", "/v2"],
  ["Marketing & Eventos", "/v2/marketing"],
  ["Producción Audiovisual", "/v2/audiovisual"],
  ["Proyectos", "/v2/proyectos"],
  ["Actualidad", "/v2/actualidad"],
  ["Contacto", "/v2/contacto"],
];

const Badge = () => (
  <span
    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em]"
    style={{ ...mont, color: COLORS.green, border: `1px solid ${COLORS.border}`, backgroundColor: "#fff" }}
    data-testid="years-badge"
  >
    <span style={{ color: COLORS.gold }}>◆</span> Desde 2007 · 20 años
  </span>
);

const V2Layout = () => {
  const [phase, setPhase] = useState(() => {
    if (typeof window === "undefined") return "intro";
    if (sessionStorage.getItem("v2_seen")) return "site";
    if (window.location.pathname !== "/v2") return "site"; // deep links skip intro
    return "intro";
  });
  const { pathname } = useLocation();
  const enter = () => {
    try { sessionStorage.setItem("v2_seen", "1"); } catch (e) {}
    setPhase("site");
  };

  return (
    <div style={{ backgroundColor: COLORS.bg, color: COLORS.ink, minHeight: "100vh", ...body }}>
      <AnimatePresence mode="wait">
        {phase === "intro" && <IntroSplash key="intro" theme={THEME} onDone={() => setPhase("gateway")} />}
        {phase === "gateway" && <MockupGateway key="gate" theme={THEME} onEnter={enter} />}
      </AnimatePresence>

      {phase === "site" && (
        <>
          <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl" style={{ backgroundColor: "rgba(251,249,245,0.9)", borderBottom: `1px solid ${COLORS.border}` }}>
            <div className="max-w-6xl mx-auto px-6">
              <div className="h-16 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Link to="/v2" className="flex items-center gap-2.5" data-testid="v2-logo">
                    <Isotipo className="w-5 h-5" fill={COLORS.green} />
                    <span className="text-base font-extrabold tracking-tight" style={mont}>GRABARTE</span>
                  </Link>
                  <span className="hidden sm:inline-flex"><Badge /></span>
                </div>
                <div className="flex items-center gap-5">
                  <span className="hidden sm:inline text-[11px] font-bold tracking-widest" style={{ ...mont, color: COLORS.mut }}><span style={{ color: COLORS.green }}>ES</span> / EN</span>
                  <Link to="/v2/contacto" data-testid="v2-cta" className="inline-flex items-center rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em]" style={{ ...mont, backgroundColor: COLORS.ink, color: "#FBF9F5" }}>Hablemos</Link>
                </div>
              </div>
              <nav className="flex items-center gap-7 overflow-x-auto no-scrollbar pb-3 -mt-1" data-testid="v2-nav">
                {NAV.map(([label, to]) => {
                  const active = pathname === to;
                  return (
                    <Link
                      key={to}
                      to={to}
                      onClick={(e) => e.currentTarget.blur()}
                      data-testid={to === "/v2" ? "v2-nav-home" : `v2-nav-${to.split("/").pop()}`}
                      className="shrink-0 text-[12px] font-medium uppercase tracking-[0.1em] transition-colors"
                      style={{ ...mont, color: active ? COLORS.green : COLORS.sec, borderBottom: active ? `2px solid ${COLORS.gold}` : "2px solid transparent", paddingBottom: 2 }}
                    >
                      {label}
                    </Link>
                  );
                })}
              </nav>
            </div>
            {/* Badge visible on mobile below nav area */}
            <div className="sm:hidden px-6 pb-2"><Badge /></div>
          </header>

          <main className="pt-36 sm:pt-28">
            <Outlet />
          </main>

          <footer className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" style={{ borderTop: `1px solid ${COLORS.border}` }}>
            <div className="flex items-center gap-3">
              <Isotipo className="w-4 h-4" fill={COLORS.green} />
              <span className="text-sm font-bold tracking-tight" style={mont}>GRABARTE</span>
              <Badge />
            </div>
            <span className="text-xs" style={{ color: COLORS.mut }}>{CONTACT.email} · © 2026 Madrid</span>
          </footer>
        </>
      )}

      <VariantSwitcher />
    </div>
  );
};

export default V2Layout;
